import { useMemo } from 'react';

import { useGetTenantsQuery } from '@features/tenants';
import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { Link, Outlet, useLocation, useParams } from 'react-router';

import { InlineLoader } from '@/components/common';

/**
 * Account Settings Layout - Sidebar listing the user's organizations.
 */
export const AccountSettingsLayout = () => {
  const location = useLocation();
  const { orgId } = useParams<{ orgId: string }>();

  // Get user's tenants
  const { data: tenants, isLoading, error } = useGetTenantsQuery();

  // Sort tenants alphabetically
  const sortedTenants = useMemo(
    () =>
      tenants && tenants.length > 0
        ? [...tenants].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
        : [],
    [tenants]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <InlineLoader size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load tenant information</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: 250,
          bgcolor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
          overflowY: 'auto',
        }}
      >
        {/* Organizations Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Organizations
          </Typography>
          <AddIcon
            fontSize="small"
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
            onClick={() => window.open('https://www.lingk.io/requestdemo', '_blank')}
          />
        </Box>

        {/* Organization List */}
        <List dense sx={{ py: 0 }}>
          {sortedTenants.map((tenant) => (
            <ListItemButton
              key={tenant.tenantId}
              selected={
                String(orgId) === String(tenant.tenantId) &&
                location.pathname.includes('/organizations/')
              }
              component={Link}
              to={`/account-settings/organizations/${tenant.tenantId}/users`}
              sx={{ py: 1 }}
            >
              <ListItemText primary={tenant.name} />
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <Outlet />
      </Box>
    </Box>
  );
};
